/**
 * Accessible progress bar (APP-002 §13, §25).
 *
 * Renders a clamped 0–100 visual fill while exposing the truthful value
 * through an accessible name + aria-valuenow. Color emphasis tracks usage so
 * warnings are color-independent (the textual value is always authoritative).
 * Respects prefers-reduced-motion (no transition under reduced motion).
 */
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** Clamped 0–100. Caller is responsible for clamping (see usagePercent). */
  value: number;
  /** Accessible label, e.g. "Monthly requests used". */
  label: string;
  /** Optional emphasis driven by usage level. */
  tone?: 'default' | 'warning' | 'danger';
}

const toneClass: Record<NonNullable<ProgressProps['tone']>, string> = {
  default: 'bg-primary',
  warning: 'bg-warning',
  danger: 'bg-destructive',
};

export function Progress({ value, label, tone = 'default', className, ...props }: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped)}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-muted', className)}
      {...props}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-300', toneClass[tone])}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
